import type { BoardColumnDef } from "@design-system/Board/Board.types";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ChevronRightSmallIcon } from "@icons";
import styles from "./TaskBoardColumnPanel.module.scss";

type StepDirection = "previous" | "next";

const STEP_LABELS: Record<StepDirection, string> = {
    previous: "Previous column",
    next: "Next column",
};

interface ColumnStepButtonProps {
    direction: StepDirection;
    /** Undefined at the end of the board: the button is disabled. */
    targetColumn: BoardColumnDef | undefined;
    onColumnChange: (columnId: string) => void;
}

const ColumnStepButton = ({
    direction,
    targetColumn,
    onColumnChange,
}: Readonly<ColumnStepButtonProps>) => (
    <Tooltip content={STEP_LABELS[direction]}>
        <Button
            variant="secondary"
            size="sm"
            iconOnly
            aria-label={STEP_LABELS[direction]}
            disabled={targetColumn === undefined}
            onClick={() => targetColumn && onColumnChange(targetColumn.id)}
        >
            <ChevronRightSmallIcon
                aria-hidden
                className={
                    direction === "previous" ? "icon-rotate-180" : undefined
                }
            />
        </Button>
    </Tooltip>
);

interface TaskBoardColumnSwitcherProps {
    columns: readonly BoardColumnDef[];
    column: BoardColumnDef;
    /** Tasks in `column`. */
    count: number;
    onColumnChange: (columnId: string) => void;
}

/**
 * The column panel's header: `‹ {column} {count} ›`, the count in the
 * column header's count tag. The arrows step
 * through the board's columns and stop at the ends.
 */
export const TaskBoardColumnSwitcher = ({
    columns,
    column,
    count,
    onColumnChange,
}: Readonly<TaskBoardColumnSwitcherProps>) => {
    const index = columns.indexOf(column);
    return (
        <HStack
            as="header"
            gap={8}
            align="center"
            justify="between"
            className={styles.switcher}
        >
            <ColumnStepButton
                direction="previous"
                targetColumn={index > 0 ? columns[index - 1] : undefined}
                onColumnChange={onColumnChange}
            />
            <HStack gap={8} align="center" className={styles.switcherLabel}>
                {column.icon !== undefined && (
                    <span aria-hidden className={styles.switcherIcon}>
                        {column.icon}
                    </span>
                )}
                <h3 aria-live="polite" className={styles.switcherTitle}>
                    <span className={styles.switcherTitleText}>
                        {column.title}
                    </span>
                    <Tag size="xs" color="neutral" numeric>
                        {count}
                    </Tag>
                </h3>
            </HStack>
            <ColumnStepButton
                direction="next"
                targetColumn={columns.at(index + 1)}
                onColumnChange={onColumnChange}
            />
        </HStack>
    );
};
